import React, { useState, useEffect, useRef } from 'react';
import { getProtocol } from '../data/protocols';
import { getSession } from '../data/guidedSessions';
import { Segment } from '../data/runs';
import { useLanguage } from '../i18n/LanguageContext';
import ProtocolWalkthrough from './ProtocolWalkthrough';
import SafetyScreen from './SafetyScreen';
import ProtocolRunner from './ProtocolRunner';
import GuidedWalkthrough from './GuidedWalkthrough';
import GuidedSessionRunner from './GuidedSessionRunner';
import TransitionBuffer from './TransitionBuffer';
import { SOUNDSCAPES, natureUrl, voiceUrl } from '../data/soundscapes';
import { checkAvailable } from '../services/audioAssets';
import { AudioExtras } from '../services/protocolAudio';
import { natureGainFor, bedScaleFor } from '../services/audioMix';
import { BackgroundBed } from '../services/backgroundBed';

type Screen = 'setup' | 'walkthrough' | 'buffer' | 'safety' | 'running' | 'done';

interface ProtocolPlayerProps {
  segments: Segment[];
  onExit: () => void;
}

const loadBool = (key: string, def: boolean): boolean => {
  try {
    const v = localStorage.getItem(key);
    return v ? JSON.parse(v) : def;
  } catch {
    return def;
  }
};

const saveBool = (key: string, value: boolean) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* ignore */
  }
};

const loadStr = (key: string, def: string): string => {
  try { return localStorage.getItem(key) ?? def; } catch { return def; }
};
const saveStr = (key: string, value: string) => {
  try { localStorage.setItem(key, value); } catch { /* ignore */ }
};
const loadNum = (key: string, def: number): number => {
  try { const v = localStorage.getItem(key); return v === null ? def : Number(v); } catch { return def; }
};

const ProtocolPlayer: React.FC<ProtocolPlayerProps> = ({ segments, onExit }) => {
  const { t, language } = useLanguage();
  const [index, setIndex] = useState(0);
  const [screen, setScreen] = useState<Screen>(segments.length > 1 ? 'setup' : 'walkthrough');
  const [soundEnabled, setSoundEnabled] = useState<boolean>(() => loadBool('protocols_soundEnabled', false));
  const [cuesEnabled, setCuesEnabled] = useState<boolean>(() => loadBool('protocols_cuesEnabled', true));
  const [soundscapeId, setSoundscapeId] = useState<string>(() => loadStr('protocols_soundscape', 'billionaire'));
  const [balance, setBalance] = useState<number>(() => loadNum('protocols_balance', 0.85));
  const [cueVolume, setCueVolume] = useState<number>(() => loadNum('protocols_cueVolume', 0.75));
  const [cueMode, setCueMode] = useState<'tone' | 'voice'>(() => (loadStr('protocols_cueMode', 'voice') === 'tone' ? 'tone' : 'voice'));
  const [counting, setCounting] = useState<'off' | 'count' | 'countWarn'>(() => {
    const v = loadStr('protocols_counting', 'count');
    return v === 'count' || v === 'countWarn' || v === 'off' ? v : 'count';
  });
  const [availNature, setAvailNature] = useState<Set<string>>(new Set());
  const [voiceReady, setVoiceReady] = useState(false);
  const bedRef = useRef<BackgroundBed | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const volume = 0.5;

  const closeAudio = () => {
    bedRef.current?.stop();
    const ctx = audioCtxRef.current;
    if (ctx && ctx.state !== 'closed') void ctx.close().catch(() => {});
    audioCtxRef.current = null;
  };

  // Pause/resume the run-level background bed alongside the rest of the session
  // (from a run's or the buffer's Pause control), without tearing it down.
  const setBedPaused = (paused: boolean) => {
    if (paused) bedRef.current?.pause();
    else bedRef.current?.resume();
  };

  useEffect(() => {
    let alive = true;
    checkAvailable(SOUNDSCAPES.map(natureUrl)).then((s) => { if (alive) setAvailNature(s); });
    const vurls = (['inhale', 'exhale', 'hold'] as const).map((c) => voiceUrl(language, c));
    checkAvailable(vurls).then((s) => { if (alive) setVoiceReady(vurls.every((u) => s.has(u))); });
    return () => { alive = false; };
  }, [language]);

  const chosen = SOUNDSCAPES.find((s) => s.id === soundscapeId && availNature.has(natureUrl(s)));

  // Run-level background bed: starts on the first buffer / run screen and plays
  // continuously across every segment (never restarting between them); stops
  // only when the run finishes or the player unmounts (exit / back to menu).
  useEffect(() => {
    const playing = screen === 'buffer' || screen === 'running' || screen === 'safety';
    if (playing && chosen) {
      if (!bedRef.current) bedRef.current = new BackgroundBed();
      bedRef.current.play(natureUrl(chosen), natureGainFor(balance) * volume);
    } else if (screen === 'done') {
      closeAudio();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen, chosen, balance]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => { closeAudio(); }, []);

  const segment = segments[index];
  if (!segment) return null;
  const protocol = segment.kind === 'protocol' ? getProtocol(segment.id) : undefined;
  const session = segment.kind === 'session' ? getSession(segment.id) : undefined;
  if (!protocol && !session) return null;

  const isRun = segments.length > 1;

  const startBed = () => {
    if (!chosen) return;
    if (!bedRef.current) bedRef.current = new BackgroundBed();
    bedRef.current.play(natureUrl(chosen), natureGainFor(balance) * volume);
  };

  // Called from the initiating tap (a trusted gesture): create and unlock the
  // run's AudioContext and start the background bed. The context is shared by
  // every segment — creating one per segment would happen after the transition
  // buffer, outside any gesture, and iOS/Safari would leave it suspended,
  // silencing cues and counting for the whole run.
  const startAudio = () => {
    try {
      const existing = audioCtxRef.current;
      if (!existing || existing.state === 'closed') {
        audioCtxRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
      if (audioCtxRef.current && audioCtxRef.current.state === 'suspended') {
        void audioCtxRef.current.resume().catch(() => {});
      }
    } catch {
      /* no Web Audio available: cues/counting silently unavailable */
    }
    startBed();
  };

  const beginRunning = () => {
    startAudio();
    if (protocol && protocol.safety && !isRun) setScreen('safety');
    else setScreen('running');
  };

  // Single play: walkthrough Start -> (safety) -> running.
  const startSegment = () => beginRunning();

  // Run: setup Start -> buffer(0) ; buffer done -> running ; complete -> next buffer or done.
  const startRun = () => { startAudio(); setScreen('buffer'); };

  const handleComplete = () => {
    if (index + 1 < segments.length) {
      setIndex(index + 1);
      setScreen(isRun ? 'buffer' : 'walkthrough');
    } else {
      setScreen('done');
    }
  };

  const toggleSound = () => setSoundEnabled((p) => { const n = !p; saveBool('protocols_soundEnabled', n); return n; });
  const toggleCues = () => setCuesEnabled((p) => { const n = !p; saveBool('protocols_cuesEnabled', n); return n; });

  const effectiveCueMode: 'tone' | 'voice' = cueMode === 'voice' && voiceReady ? 'voice' : 'tone';
  const extras: AudioExtras = {
    bedScale: chosen ? bedScaleFor(balance) : 1,
    cueVolume,
    cueMode: effectiveCueMode,
    voiceUrls: {
      inhale: voiceUrl(language, 'inhale'),
      exhale: voiceUrl(language, 'exhale'),
      hold: voiceUrl(language, 'hold'),
    },
    counting,
  };

  const onSoundscape = (id: string) => { setSoundscapeId(id); saveStr('protocols_soundscape', id); };
  const onBalance = (v: number) => { setBalance(v); saveStr('protocols_balance', String(v)); };
  const onCueVolume = (v: number) => { setCueVolume(v); saveStr('protocols_cueVolume', String(v)); };
  const onCueMode = (m: 'tone' | 'voice') => { setCueMode(m); saveStr('protocols_cueMode', m); };
  const changeCounting = (v: 'off' | 'count' | 'countWarn') => {
    setCounting(v);
    saveStr('protocols_counting', v);
  };

  const startLabel = isRun ? t('protocols.startWhenReady') : t('protocols.start');

  const settingsControls = (
    <>
      <label className="text-sm text-stone-500 flex items-center gap-2">
        <input type="checkbox" checked={soundEnabled} onChange={toggleSound} />
        {t('protocols.sound')}
      </label>
      <label className="text-sm text-stone-500 flex items-center gap-2">
        <input type="checkbox" checked={cuesEnabled} onChange={toggleCues} />
        {t('protocols.cues')}
      </label>
      <label className="text-sm text-stone-500 flex items-center justify-between gap-2">
        {t('protocols.background')}
        <select
          value={soundscapeId}
          onChange={(e) => onSoundscape(e.target.value)}
          className="bg-white/70 rounded-lg px-2 py-1 text-stone-700"
        >
          <option value="">{t('protocols.backgroundNone')}</option>
          {SOUNDSCAPES.filter((s) => availNature.has(natureUrl(s))).map((s) => (
            <option key={s.id} value={s.id}>{language === 'en' ? s.name.en : s.name.no}</option>
          ))}
        </select>
      </label>
      {chosen && (
        <label className="text-sm text-stone-500 flex items-center gap-2">
          {t('protocols.balance')}
          <input
            type="range" min={0} max={1} step={0.05} value={balance}
            onChange={(e) => onBalance(Number(e.target.value))}
            className="flex-1"
          />
        </label>
      )}
      <label className="text-sm text-stone-500 flex items-center gap-2">
        {t('protocols.cueVolume')}
        <input
          type="range" min={0} max={1} step={0.05} value={cueVolume}
          onChange={(e) => onCueVolume(Number(e.target.value))}
          className="flex-1"
        />
      </label>
      {cuesEnabled && voiceReady && (
        <label className="text-sm text-stone-500 flex items-center gap-2">
          <input
            type="checkbox"
            checked={cueMode === 'voice'}
            onChange={(e) => onCueMode(e.target.checked ? 'voice' : 'tone')}
          />
          {t('protocols.cueVoice')}
        </label>
      )}
      <div className="text-sm text-stone-600">
        <span className="block mb-1">{t('protocols.counting')}</span>
        <div className="flex gap-2">
          {(['off', 'count', 'countWarn'] as const).map((v) => (
            <button
              key={v}
              onClick={() => changeCounting(v)}
              className={`px-3 py-1.5 rounded-full text-xs ${counting === v ? 'bg-accent text-white' : 'bg-white/70 text-stone-600'}`}
            >
              {t('protocols.counting_' + v)}
            </button>
          ))}
        </div>
      </div>
    </>
  );

  return (
    <div className="bg-white/60 p-6 sm:p-8 rounded-4xl shadow-lg border border-white/50">
      {isRun && screen !== 'done' && (
        <p className="text-xs uppercase tracking-wide text-stone-400 mb-4">
          {t('protocols.stepOf').replace('{n}', String(index + 1)).replace('{total}', String(segments.length))}
        </p>
      )}

      {screen === 'walkthrough' && (
        <>
          {protocol && <ProtocolWalkthrough protocol={protocol} onStart={startSegment} startLabel={startLabel} />}
          {session && <GuidedWalkthrough session={session} onStart={startSegment} startLabel={startLabel} />}
          <div className="mt-4 flex flex-col gap-2 border-t border-stone-200 pt-4">
            {settingsControls}
            <button onClick={onExit} className="text-sm text-stone-500 hover:text-stone-700 self-end">
              {t('protocols.back')}
            </button>
          </div>
        </>
      )}

      {screen === 'setup' && (
        <div>
          <h2 className="text-3xl font-bold mb-1">{t('protocols.runReady')}</h2>
          <p className="text-stone-600 mb-4">{t('protocols.runReadyDesc')}</p>
          <div className="flex flex-col gap-2 border-t border-stone-200 pt-4">
            {settingsControls}
          </div>
          <button
            onClick={startRun}
            className="w-full mt-4 px-6 py-3 text-base font-medium text-white bg-accent rounded-full hover:bg-accent-dark"
          >
            {t('protocols.startRun')}
          </button>
          <button onClick={onExit} className="w-full mt-2 text-sm text-stone-500 hover:text-stone-700">
            {t('protocols.back')}
          </button>
        </div>
      )}

      {screen === 'buffer' && (
        <TransitionBuffer segment={segment} onDone={() => setScreen('running')} onQuit={onExit} onPauseChange={setBedPaused} />
      )}

      {screen === 'safety' && protocol && (
        <SafetyScreen
          protocol={protocol}
          onContinue={() => setScreen('running')}
          onCancel={() => setScreen('walkthrough')}
        />
      )}

      {screen === 'running' && protocol && (
        <ProtocolRunner
          key={`p-${protocol.id}`}
          protocol={protocol}
          soundEnabled={soundEnabled}
          cuesEnabled={cuesEnabled}
          volume={volume}
          extras={extras}
          audioCtx={audioCtxRef.current}
          onComplete={handleComplete}
          onQuit={onExit}
          onPauseChange={setBedPaused}
        />
      )}

      {screen === 'running' && session && (
        <GuidedSessionRunner
          key={`s-${session.id}`}
          session={session}
          soundEnabled={soundEnabled}
          cuesEnabled={cuesEnabled}
          volume={volume}
          extras={extras}
          audioCtx={audioCtxRef.current}
          onComplete={handleComplete}
          onQuit={onExit}
          onPauseChange={setBedPaused}
        />
      )}

      {screen === 'done' && (
        <div className="text-center py-8">
          <h2 className="text-3xl font-bold mb-3">{t('protocols.doneTitle')}</h2>
          <p className="text-stone-600 mb-8">{t('protocols.doneSubtitle')}</p>
          <button onClick={onExit} className="px-8 py-3 rounded-full text-base font-semibold text-white bg-accent hover:bg-accent-dark">
            {t('protocols.finish')}
          </button>
        </div>
      )}
    </div>
  );
};

export default ProtocolPlayer;
